import { normalizeMCPUrl } from '@littlebird/mcp';

import {
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

type Props = {
  // `url` is optional because tool-call modalities only carry the server
  // name/slug. When present it's the more specific match; otherwise we fall
  // back to case-insensitive name matching against the catalog and servers.
  server: { name: string; url?: string };
  className?: string;
};

function findByUrlThenName<T extends { name: string; url: string }>(
  entries: T[],
  server: { name: string; url?: string }
): T | undefined {
  return entries.find((entry) => {
    if (
      server.url &&
      normalizeMCPUrl(entry.url) === normalizeMCPUrl(server.url)
    ) {
      return true;
    }
    return entry.name.toLowerCase() === server.name.toLowerCase();
  });
}

// Catalog icon wins over the server's own advertised icon_url. enabled=false
// so this decorative icon never triggers the servers query's probing fetch.
export const MCPIcon = ({ server, className }: Props) => {
  const { data: catalogEntries } = useMCPCatalogQuery();
  const { data: servers } = useMCPServersQuery(false);

  const catalogMatch = findByUrlThenName(catalogEntries ?? [], server);
  // Multi-account servers share a name across sibling rows, so a name-only
  // lookup must skip rows with no icon to avoid landing on the wrong sibling.
  const serverMatch = findByUrlThenName(
    (servers ?? []).filter((s) => s.icon_url),
    server
  );
  const iconUrl = catalogMatch?.icon_url || serverMatch?.icon_url;

  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={cn('rounded object-contain shrink-0', className)}
      />
    );
  }
  const FallbackIcon = getIntegrationIcon(server.name);
  return <FallbackIcon className={className} />;
};
